import React, { useMemo, useState } from "react";
import { Check, ChevronDown, ChevronsUpDown } from "lucide-react";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  PHONE_COUNTRY_OPTIONS,
  buildPhoneFieldValue,
  getCountryFlagEmoji,
  getCountryFlagSrc,
  getPhoneFieldError,
  searchPhoneCountryOptions,
  touchPhoneField,
  updatePhoneFieldCountry,
  updatePhoneFieldNumber,
} from "../lib/phone";

const THEMES = {
  light: {
    shell: "border-slate-200 bg-white focus-within:border-slate-300 focus-within:ring-slate-900/10",
    field: "bg-transparent text-slate-900 placeholder:text-slate-300",
    select: "border-slate-200/90 text-slate-900",
    compactField: "bg-transparent text-slate-700",
    hint: "text-slate-500",
    error: "text-red-600",
    dropdown: "border-slate-200 bg-white shadow-xl shadow-slate-200/70",
    item: "data-[selected=true]:bg-sky-50 data-[selected=true]:text-slate-900",
    code: "text-slate-500",
  },
  dark: {
    shell: "border-white/10 bg-white/5 focus-within:border-white/20 focus-within:ring-white/10",
    field: "bg-transparent text-white placeholder:text-slate-500",
    select: "border-white/10 text-white",
    compactField: "bg-transparent text-slate-200",
    hint: "text-slate-400",
    error: "text-rose-300",
    dropdown: "border-white/10 bg-slate-950 text-white shadow-2xl shadow-black/30",
    item: "data-[selected=true]:bg-white/10 data-[selected=true]:text-white",
    code: "text-slate-400",
  },
};

function CountryFlag({ country, alt }) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return <span className="text-lg leading-none" aria-hidden="true">{getCountryFlagEmoji(country)}</span>;
  }

  return (
    <img
      src={getCountryFlagSrc(country)}
      alt={alt}
      className="h-4 w-5 rounded-[2px] object-cover shadow-sm"
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

export function PhoneField({
  value,
  onChange,
  theme = "light",
  countryTestId,
  numberTestId,
  errorTestId,
  externalError = "",
}) {
  const current = value?.country ? value : buildPhoneFieldValue();
  const styles = THEMES[theme] || THEMES.light;
  const error = externalError || (current.touched ? getPhoneFieldError(current) : "");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selectedCountry = useMemo(
    () => PHONE_COUNTRY_OPTIONS.find((country) => country.value === current.country) || PHONE_COUNTRY_OPTIONS[0],
    [current.country]
  );
  const visibleCountries = useMemo(() => searchPhoneCountryOptions(query), [query]);

  return (
    <div className="space-y-2">
      <div
        className={`flex h-10 w-full overflow-hidden rounded-[1rem] border shadow-sm transition-colors focus-within:ring-4 ${styles.shell} ${error ? "border-red-300 focus-within:border-red-300 focus-within:ring-red-100" : ""}`}
      >
        <Popover
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (!next) setQuery("");
          }}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              role="combobox"
              data-testid={countryTestId}
              className={`flex h-full w-[76px] shrink-0 items-center justify-between border-r px-2 sm:w-[82px] ${styles.select}`}
            >
              <span className="flex min-w-0 items-center gap-1.5 text-left">
                <CountryFlag country={selectedCountry.value} alt={`${selectedCountry.name} flag`} />
                <span className={`shrink-0 text-[11px] font-semibold tracking-[0.01em] ${styles.code}`}>{selectedCountry.callingCode}</span>
              </span>
              <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-45" />
            </button>
          </PopoverTrigger>
          <PopoverContent className={`w-[300px] max-w-[90vw] rounded-xl p-0 ${styles.dropdown}`} align="start">
            <Command shouldFilter={false}>
              <CommandInput
                value={query}
                onValueChange={setQuery}
                placeholder="Search country or code"
                data-testid={countryTestId ? `${countryTestId}-search` : undefined}
                className="h-11 text-sm"
              />
              <CommandList>
                <CommandEmpty>No country found.</CommandEmpty>
                {visibleCountries.map((country) => (
                  <CommandItem
                    key={country.value}
                    value={country.searchText}
                    onSelect={() => {
                      onChange(updatePhoneFieldCountry(current, country.value));
                      setOpen(false);
                      setQuery("");
                    }}
                    data-testid={countryTestId ? `${countryTestId}-option-${country.value}` : undefined}
                    className={`gap-3 px-3 py-2.5 ${styles.item}`}
                  >
                    <Check className={`h-4 w-4 ${country.value === current.country ? "opacity-100" : "opacity-0"}`} />
                    <CountryFlag country={country.value} alt={`${country.name} flag`} />
                    <span className="flex-1 truncate font-medium">{country.name}</span>
                    <span className={`text-xs font-medium ${styles.code}`}>{country.callingCode}</span>
                  </CommandItem>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <input
          value={current.nationalNumber}
          onChange={(e) => onChange(updatePhoneFieldNumber(current, e.target.value))}
          onBlur={() => onChange(touchPhoneField(current))}
          data-testid={numberTestId}
          inputMode="numeric"
          autoComplete="tel-national"
          placeholder={current.placeholder}
          aria-invalid={!!error}
          className={`h-full min-w-0 flex-1 px-3.5 text-sm font-medium tracking-tight outline-none sm:px-4 sm:text-base ${styles.field}`}
        />
      </div>
      <div className="min-h-[18px]">
        {error ? (
          <p data-testid={errorTestId} className={`text-xs ${styles.error}`}>
            {error}
          </p>
        ) : current.placeholder ? (
          <p className={`text-xs ${styles.hint}`}>Example: {current.placeholder}</p>
        ) : null}
      </div>
    </div>
  );
}

export function PreferredContactMethodField({ value, onChange, testid, theme = "light" }) {
  const styles = THEMES[theme] || THEMES.light;
  return (
    <div className={`relative h-10 w-full overflow-hidden rounded-[1rem] border shadow-sm transition-colors focus-within:ring-4 ${styles.shell}`}>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value || null)}
        data-testid={testid}
        className={`h-full w-full appearance-none px-3.5 pr-10 text-sm font-medium outline-none ${styles.compactField}`}
      >
        <option value="">No preference</option>
        <option value="call">Call</option>
        <option value="whatsapp">WhatsApp</option>
      </select>
      <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 opacity-45" />
    </div>
  );
}
